import PropTypes from 'prop-types'
import React, { Component } from 'react'
import classnames from 'classnames'
import camelCase from 'lodash.camelcase'
import { SubmissionError } from 'redux-form'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'
import SignupForm from './SignupForm'
import { Loader } from 'components/util'
import styles from './_signup.css'
import * as auth from 'services/auth/selectors'
import * as bootupSelectors from 'services/bootup/selectors'
import * as site from 'services/site/selectors'
import * as consents from 'services/consents/selectors'
import * as authActions from 'services/auth/actions'
import OnboardingModal from './OnboardingModal'
import { FORM_NAME } from './SignupForm'
import { FB_ONBOARDING, UACF_ONBOARDING, UACF_FB_ONBOARDING } from 'services/auth/constants'
import ageFromBirthdate from 'shared/units/metrics/age'
import { loadFromLocal, saveToLocal } from 'shared/localStorage'
import { selectConsentDetailsForIsoCode } from 'shared/consentsUtil'
import * as landingActions from 'services/landing/actions'
import FacebookSignupButton from './FacebookSignupButton'
import WechatSignupButton from './WechatSignupButton'

const mapStateToProps = (state) => ({
  appConfig: bootupSelectors.selectAppConfig(state),
  onboarding: auth.selectOnboarding(state),
  siteName: site.selectSiteName(state),
  consentMatrix: consents.selectConsentMatrix(state),
  promptOnboarding: auth.selectPromptOnboarding(state),
  isWechatAuth: auth.selectIsWechatAuth(state),
})

const mapDispatchToActions = {
  ...authActions,
  ...landingActions,
}

export class Signup extends Component {

  static propTypes = {
    appConfig: PropTypes.object,
    leftAlign: PropTypes.bool,
    onClickLogin: PropTypes.func,
    onSignupSuccess: PropTypes.func,
    onboarding: PropTypes.string,
    siteName: PropTypes.string,
    stashEmailForOnboarding: PropTypes.func,
    reload: PropTypes.bool,
    consentMatrix: PropTypes.object,
    stageUser: PropTypes.func,
    onSignupConsents: PropTypes.func,
    signupUser: PropTypes.func.isRequired,
    cancelOnboardingPrompt: PropTypes.func,
    promptOnboarding: PropTypes.bool,
    findUserByEmail: PropTypes.func,
    logIn: PropTypes.func,
    isWechatAuth: PropTypes.bool,
  }

  static defaultProps = {
    reload: false,
  }

  constructor (props) {
    super(props)
    this.setSignupError = this.setSignupError.bind(this)
    this.setSignupSubmitting = this.setSignupSubmitting.bind(this)
  }

  state = {
    submitting: false,
  }

  setSignupSubmitting (submitting) {
    this.setState({ submitting, error: null })
  }

  setSignupError (error) {
    this.setState({ submitting: false, error: error })
  }

  handleSubmit = (values) => {
    const { consentMatrix, onboarding, reload } = this.props
    const { birthdate, gdpr_iso_code } = values
    const age = ageFromBirthdate(Date.parse(birthdate))
    const consent = selectConsentDetailsForIsoCode(consentMatrix, gdpr_iso_code)
    const minimumAge = parseInt(consent.age, 10)
    const isUnderage = loadFromLocal('isUnderage')

    if (age < minimumAge || isUnderage) {
      this.setSignupError(true)
      saveToLocal('isUnderage', true, 300000) // 5 minutes
      return Promise.reject().catch(() => { throw new SubmissionError({ _error: 'birthdate/ineligible' }) })
    }
    const userData = { ...values, consent_matrix_version: consentMatrix.version }
    const uacfOnboarding = onboarding === UACF_ONBOARDING || onboarding === UACF_FB_ONBOARDING
    this.setSignupSubmitting(true)
    if (consent.standard !== 'US') {
      this.props.stageUser(userData)
      if (uacfOnboarding) {
        return this.props.onSignupConsents()
      } else {
        return this.props.findUserByEmail(userData.email)
        .then(() => {
          this.setSignupSubmitting(false)
          if (!this.props.promptOnboarding) {
            return this.props.onSignupConsents()
          }
        })
      }
    } else {
      return this.props.signupUser(onboarding, userData, reload)
        .then(this.props.onSignupSuccess)
        .catch((err) => {
          this.setSignupError(true)
          // common server errors like: email in use, or age restriction
          if (!this.props.promptOnboarding && err.response && err.response.data && err.response.data.error) {
            throw new SubmissionError({ _error: err.response.data.error })
          }

          // catch-all for other validation issues
          if (err.response
            && err.response.data
            && err.response.data._diagnostics
            && err.response.data._diagnostics.validation_failures
            && err.response.data._diagnostics.validation_failures.length
            && err.response.data._diagnostics.validation_failures[0].length
            && err.response.data._diagnostics.validation_failures[0][0].validation
          ) {
            const errors = err.response.data._diagnostics.validation_failures[0][0].validation
            const errorObj = this.createSubmissionError(errors)
            throw new SubmissionError(errorObj)
          }
        })
    }
  }

  createSubmissionError = (errors) => {
    const errorObj = Object.keys(errors)
      .reduce((prev, curr) => {
        const key = camelCase(curr)
        prev[key] = errors[curr].join(', ')
        return prev
      }, {})

    return errorObj
  }

  handleContinueOnboarding = () => {
    const { onClickLogin, cancelOnboardingPrompt } = this.props
    this.props.stashEmailForOnboarding(FORM_NAME)
    if (onClickLogin) {
      cancelOnboardingPrompt() // Cancel the onboarding promt flag before going to login Screen
      onClickLogin()
    }
  }

  handleClickLogin = (evt) => {
    const { onClickLogin, logIn } = this.props
    evt.preventDefault()
    logIn('login_in_auth')
    if (onClickLogin) { onClickLogin(evt) }
  }

  render () {
    const {
      appConfig,
      leftAlign,
      onboarding,
      siteName,
      promptOnboarding,
      cancelOnboardingPrompt,
      isWechatAuth,
    } = this.props
    const { submitting } = this.state
    const fbOnboarding = onboarding === FB_ONBOARDING
    const uacfOnboarding = onboarding === UACF_ONBOARDING || onboarding === UACF_FB_ONBOARDING

    return (
      <div className={styles.container}>

        <OnboardingModal
          effect='down'
          isOpen={promptOnboarding}
          onRequestedClose={cancelOnboardingPrompt}
          onContinue={this.handleContinueOnboarding}
        />

        <ReactCSSTransitionGroup
          component='div'
          transitionName='fade'
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
        >
          {submitting ?
            <div key='loader'>
              <div className={styles.loader}>
                <Loader height={60} width={60} />
              </div>
            </div>
          : null}
        </ReactCSSTransitionGroup>

        {!onboarding && !isWechatAuth ?
          <a
            href='/auth/login'
            onClick={this.handleClickLogin}
            className={classnames(styles.alternateLink, {
              [styles.leftAlign]: leftAlign,
            })}
          >
            <FormattedMessage
              id='auth.signup.log_in'
              defaultMessage='Log In'
            />
          </a>
        : null}

        {uacfOnboarding || isWechatAuth ?
          <div className={styles.onboarding}>
            <h2 className={styles.onboardingTitle}>
              <FormattedMessage
                id='auth.signup.onboarding.title'
                defaultMessage='Welcome to {siteName}'
                values={{ siteName }}
              />
            </h2>
            <p className={styles.onboardingDescription}>
              <FormattedMessage
                id='auth.signup.onboarding.description'
                defaultMessage='We need a few more details about you to complete your {siteName} profile.'
                values={{ siteName }}
              />
            </p>
          </div>
        : null}

        {!uacfOnboarding && !isWechatAuth ?
          <div>
            {appConfig.isChina ?
              <WechatSignupButton
                disabled={submitting}
              />
            :
              <FacebookSignupButton
                disableClick={submitting || fbOnboarding}
                onboarding={onboarding}
                setSignupError={this.setSignupError}
                setSignupSubmitting={this.setSignupSubmitting}
              />}

            <div className={styles.or}>
              <span className={styles.hr} />
              <span className={styles.orText}>
                <FormattedMessage id='auth.signup.or' defaultMessage='or' />
              </span>
              <span className={styles.hr} />
            </div>
          </div>
        : null}

        <SignupForm onSubmit={this.handleSubmit} signingUp={submitting} showSpinner={this.setSignupSubmitting} />

      </div>
    )
  }

}

export default connect(mapStateToProps, mapDispatchToActions)(Signup)

